/**
 * Scales each [data-ct-scale] canvas from a 1440px design root
 * so the composition looks identical at every desktop viewport width.
 * Prefers CSS zoom (layout-aware). Falls back to transform + height.
 * Below BREAKPOINT, scaling is disabled and sections use fluid CSS.
 *
 * First paint is handled by a blocking head bootstrap + ct-scale.css.
 * This file only keeps JS-driven sync (resize, fonts, images, Firefox height).
 */
(() => {
  const ROOT = 1440;
  const BREAKPOINT = 1200;
  const SUPPORTS_ZOOM = typeof CSS !== 'undefined' && CSS.supports && CSS.supports('zoom', '1');

  /** Keep html data attrs in sync (also set by head bootstrap). */
  const syncRoot = () => {
    const vw = window.innerWidth;
    const fluid = vw < BREAKPOINT;
    const scale = fluid ? 1 : Math.max(vw / ROOT, 0.01);
    const root = document.documentElement;
    root.style.setProperty('--ct-page-scale', String(scale));
    root.dataset.ctScale = fluid ? 'fluid' : 'active';
    root.dataset.ctScaleReady = 'true';
    return { vw, fluid, scale };
  };

  const reset = (wrap, canvas) => {
    wrap.classList.add('ct-scale--fluid');
    wrap.classList.remove('ct-scale--active');
    wrap.style.height = '';
    canvas.style.width = '';
    canvas.style.transform = '';
    canvas.style.transformOrigin = '';
    canvas.style.zoom = '';
  };

  const applyOne = (wrap, scale, fluid) => {
    if (!(wrap instanceof HTMLElement)) return;
    const canvas = wrap.querySelector('[data-ct-scale-canvas]');
    if (!(canvas instanceof HTMLElement)) return;

    if (fluid) {
      reset(wrap, canvas);
      return;
    }

    wrap.classList.remove('ct-scale--fluid');
    wrap.classList.add('ct-scale--active');
    canvas.style.width = `${ROOT}px`;

    if (SUPPORTS_ZOOM) {
      // Prefer CSS variable zoom from head; still set inline as source of truth after resize
      canvas.style.zoom = String(scale);
      canvas.style.transform = '';
      wrap.style.height = '';
      return;
    }

    canvas.style.zoom = '';
    canvas.style.transform = `scale(${scale})`;
    canvas.style.transformOrigin = 'top center';

    // Header must hug content — never reserve transform leftover height
    if (wrap.closest('[data-ct-header]')) {
      wrap.style.height = '';
      return;
    }

    const natural = canvas.scrollHeight || canvas.offsetHeight;
    wrap.style.height = `${natural * scale}px`;
  };

  const syncHeaderHeight = () => {
    const header = document.querySelector('[data-ct-header]');
    if (!(header instanceof HTMLElement)) return;
    const scaled = header.querySelector('[data-ct-scale]');
    const height = (scaled instanceof HTMLElement ? scaled.offsetHeight : header.offsetHeight) || 0;
    document.body.style.setProperty('--header-height', `${height}px`);
    document.body.style.setProperty('--top-row-height', `${height}px`);
  };

  const apply = () => {
    const { scale, fluid } = syncRoot();
    document.querySelectorAll('[data-ct-scale]').forEach((wrap) => applyOne(wrap, scale, fluid));
    syncHeaderHeight();
    window.dispatchEvent(new CustomEvent('ct:scale'));
  };

  let ticking = false;
  const onResize = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      apply();
      ticking = false;
    });
  };

  const boot = () => {
    apply();
    // One short reflow pass for late layout (fonts/images) — avoid multi-second delays
    window.setTimeout(apply, 80);
  };

  // Run as soon as possible — modules/defer may already be after interactive
  if (document.readyState === 'loading') {
    // Apply root vars again immediately; paint already used head bootstrap
    syncRoot();
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.addEventListener('resize', onResize, { passive: true });
  window.addEventListener('load', apply);
  window.addEventListener('shopify:section:load', () => {
    window.setTimeout(apply, 30);
  });

  document.addEventListener(
    'load',
    (event) => {
      const t = event.target;
      if (t instanceof HTMLImageElement && t.closest('[data-ct-scale-canvas]')) {
        apply();
      }
    },
    true
  );

  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(apply).catch(() => {});
  }
})();
